import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { IntervalDropdowns, defaultInterval } from '../../../../../admin/components/IntervalDropdowns';
import { Button } from '../../../../../web/components-v2/Button';
import { Checkbox } from '../../../../../web/components-v2/Checkbox';
import { Dialog, DialogBody, DialogFooter, DialogPopup, DialogTitle } from '../../../../../web/components-v2/Dialog';
import { businessHoursDialogStyles as styleSheet } from './businessHoursDialogStyles';

export interface IBusinessHoursDialogProps {
	open: boolean;
	sendEmailFeatures?: Api.ISendEmailFeatures; // current values to seed the form
	isSaving: boolean; // disable Save while persisting
	onClose: () => void;
	onSave: (features: Partial<Api.ISendEmailFeatures>) => void; // emit only the interval-related fields
}

interface IBusinessHoursFormState {
	mondayInterval?: Api.IInterval;
	tuesdayInterval?: Api.IInterval;
	wednesdayInterval?: Api.IInterval;
	thursdayInterval?: Api.IInterval;
	fridayInterval?: Api.IInterval;
	saturdayInterval?: Api.IInterval;
	sundayInterval?: Api.IInterval;
	weekdaysInterval?: Api.IInterval;
	useDiscreteIntervals: boolean;
}

// Weekdays is edited/validated separately (see onUpdateWeekdays), so it is excluded here.
type DayIntervalKey =
	| 'mondayInterval'
	| 'tuesdayInterval'
	| 'wednesdayInterval'
	| 'thursdayInterval'
	| 'fridayInterval'
	| 'saturdayInterval'
	| 'sundayInterval';

const buildInitialState = (features?: Api.ISendEmailFeatures): IBusinessHoursFormState => ({
	mondayInterval: features?.mondayInterval,
	tuesdayInterval: features?.tuesdayInterval,
	wednesdayInterval: features?.wednesdayInterval,
	thursdayInterval: features?.thursdayInterval,
	fridayInterval: features?.fridayInterval,
	saturdayInterval: features?.saturdayInterval,
	sundayInterval: features?.sundayInterval,
	weekdaysInterval: features?.weekdaysInterval ?? defaultInterval,
	useDiscreteIntervals: features?.useDiscreteIntervals ?? false,
});

export const BusinessHoursDialog: React.FC<IBusinessHoursDialogProps> = ({
	open,
	sendEmailFeatures,
	isSaving,
	onClose,
	onSave,
}) => {
	const checkboxId = React.useId();
	const [state, setState] = React.useState<IBusinessHoursFormState>(() => buildInitialState(sendEmailFeatures));

	// Re-seed the form each time the dialog opens so a reopened dialog reflects the latest
	// persisted values (mirrors the legacy modal, which reset its state on close).
	React.useEffect(() => {
		if (open) {
			setState(buildInitialState(sendEmailFeatures));
		}
	}, [open, sendEmailFeatures]);

	const {
		mondayInterval,
		tuesdayInterval,
		wednesdayInterval,
		thursdayInterval,
		fridayInterval,
		saturdayInterval,
		sundayInterval,
		weekdaysInterval,
		useDiscreteIntervals,
	} = state;

	const onUpdateInterval = (key: DayIntervalKey, interval: Api.IInterval) => {
		// Removing an interval (undefined) is always allowed; otherwise the range must be ordered.
		if (!interval || interval.startMinutes < interval.endMinutes) {
			setState(prev => ({ ...prev, [key]: interval }));
		} else {
			toast.error('End time must be greater than start time.');
		}
	};

	const onUpdateWeekdays = (updatedWeekdaysInterval: Api.IInterval) => {
		if (updatedWeekdaysInterval && updatedWeekdaysInterval.startMinutes < updatedWeekdaysInterval.endMinutes) {
			setState(prev => ({ ...prev, weekdaysInterval: updatedWeekdaysInterval }));
		} else {
			toast.error('End time must be greater than start time.');
		}
	};

	const handleSave = () => {
		onSave({
			mondayInterval,
			tuesdayInterval,
			wednesdayInterval,
			thursdayInterval,
			fridayInterval,
			saturdayInterval,
			sundayInterval,
			weekdaysInterval,
			useDiscreteIntervals,
			// Saving from this dialog always enables business-hours observance — the intervals are
			// meaningless if unobserved (parity with the modernized web BusinessHoursModal).
			observeSendIntervals: true,
		});
	};

	return (
		<Dialog open={open} onOpenChange={o => !o && onClose()} dismissOnOutsideClick={false}>
			<DialogPopup styles={[styleSheet.dialogPopup]}>
				<DialogTitle>Manage Business Hours for this Account</DialogTitle>
				<DialogBody styles={[styleSheet.body]}>
					<div className={css(styleSheet.checkboxRow)}>
						<Checkbox
							id={checkboxId}
							checked={useDiscreteIntervals}
							disabled={isSaving}
							onCheckedChange={checked => setState(prev => ({ ...prev, useDiscreteIntervals: checked === true }))}
						/>
						<label htmlFor={checkboxId} className={css(styleSheet.checkboxLabel)}>
							Set individual business hours for each day of the week
						</label>
					</div>

					{useDiscreteIntervals ? (
						<>
							<div className={css(styleSheet.header)}>Monday</div>
							<IntervalDropdowns
								canRemove={true}
								disabled={isSaving}
								interval={mondayInterval}
								isFriday={false}
								onUpdateInterval={interval => onUpdateInterval('mondayInterval', interval)}
							/>
							<div className={css(styleSheet.header)}>Tuesday</div>
							<IntervalDropdowns
								canRemove={true}
								disabled={isSaving}
								interval={tuesdayInterval}
								isFriday={false}
								onUpdateInterval={interval => onUpdateInterval('tuesdayInterval', interval)}
							/>
							<div className={css(styleSheet.header)}>Wednesday</div>
							<IntervalDropdowns
								canRemove={true}
								disabled={isSaving}
								interval={wednesdayInterval}
								isFriday={false}
								onUpdateInterval={interval => onUpdateInterval('wednesdayInterval', interval)}
							/>
							<div className={css(styleSheet.header)}>Thursday</div>
							<IntervalDropdowns
								canRemove={true}
								disabled={isSaving}
								interval={thursdayInterval}
								isFriday={false}
								onUpdateInterval={interval => onUpdateInterval('thursdayInterval', interval)}
							/>
						</>
					) : (
						<>
							<div className={css(styleSheet.header)}>Weekdays (Monday - Thursday)</div>
							<IntervalDropdowns disabled={isSaving} interval={weekdaysInterval} onUpdateInterval={onUpdateWeekdays} />
						</>
					)}
					<div className={css(styleSheet.header)}>Friday</div>
					<IntervalDropdowns
						canRemove={true}
						disabled={isSaving}
						interval={fridayInterval}
						isFriday={!useDiscreteIntervals}
						onUpdateInterval={interval => onUpdateInterval('fridayInterval', interval)}
					/>
					<div className={css(styleSheet.header)}>Saturday</div>
					<IntervalDropdowns
						canRemove={true}
						disabled={isSaving}
						interval={saturdayInterval}
						onUpdateInterval={interval => onUpdateInterval('saturdayInterval', interval)}
					/>
					<div className={css(styleSheet.header)}>Sunday</div>
					<IntervalDropdowns
						canRemove={true}
						disabled={isSaving}
						interval={sundayInterval}
						onUpdateInterval={interval => onUpdateInterval('sundayInterval', interval)}
					/>
				</DialogBody>
				<DialogFooter>
					<Button variant='secondary' size='sm' onClick={onClose} disabled={isSaving}>
						Cancel
					</Button>
					<Button variant='primary' size='sm' onClick={handleSave} isLoading={isSaving} disabled={isSaving}>
						Save
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
